import { config } from "@/config";

import { ProjetInfo } from "@/types/project";
import { useQuery } from "@tanstack/react-query";
import { isUuid } from "@/lib/uuid";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { apiFetch } from "@/lib/api-client";
import { useUser } from "@/hooks/use-user";

export default function useGetProjectById(uuid: string, enabled?: boolean) {
  const { getAuthHeaders } = useAuthHeaders();
  const { user } = useUser();
  const isAdmin = user?.role === "ADMIN";

  const getProjectByUUId = async (
    uuid: string,
    admin: boolean
  ): Promise<ProjetInfo | undefined> => {
    let url = `${config.isometricApiUrl}/projects/accessible?filters[uuid][$eq]=${uuid}`;
    if (admin) {
      url += `&allProjects=true`;
    }

    try {
      const response = await apiFetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result?.data?.[0] ?? {};
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return useQuery({
    queryKey: ["project", uuid, isAdmin],
    queryFn: () => {
      return getProjectByUUId(uuid, !!isAdmin);
    },
    // a malformed id never reaches the API (it only gave a 400) — BREEZEAI-1471 BUG-11
    enabled: !!enabled && user !== undefined && isUuid(uuid),
    // The breadcrumb bar and the page both read the project; without a freshness window the one that mounts
    // second fetches it again (BREEZEAI-1471 BUG-6). Edits invalidate ["project"], so this never hides a change.
    staleTime: 30_000,
  });
}
